import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
interface EditTicketModalProps {
  onClose: () => void;
  onSave: (ticketData: any) => void;
  ticket: any;
}
const EditTicketModal: React.FC<EditTicketModalProps> = ({
  onClose,
  onSave,
  ticket
}) => {
  const [ticketData, setTicketData] = useState({
    ticketNumber: '',
    paxName: '',
    sector: '',
    sectorCode: '',
    airlineCode: '',
    class: '',
    issueDate: '',
    airFare: '',
    tax: '',
    otherBkgRef: '',
    remark: ''
  });
  const [errors, setErrors] = useState({
    ticketNumber: '',
    paxName: '',
    airlineCode: '',
    airFare: ''
  });
  // Initialize form with ticket data
  useEffect(() => {
    if (ticket) {
      setTicketData({
        ticketNumber: ticket.ticketNumber || '',
        paxName: ticket.paxName || '',
        sector: ticket.sector || '',
        sectorCode: ticket.sectorCode || '',
        airlineCode: ticket.airlineCode || '',
        class: ticket.class || '',
        issueDate: ticket.issueDate || '',
        airFare: ticket.airFare ? String(ticket.airFare) : '',
        tax: ticket.tax ? String(ticket.tax) : '',
        otherBkgRef: ticket.otherBkgRef || '',
        remark: ticket.remark || ''
      });
    }
  }, [ticket]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setTicketData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    if (!ticketData.ticketNumber) {
      newErrors.ticketNumber = 'Ticket number is required';
      isValid = false;
    } else if (!/^\d{13}$/.test(ticketData.ticketNumber)) {
      newErrors.ticketNumber = 'Ticket number must be 13 digits';
      isValid = false;
    }
    if (!ticketData.paxName) {
      newErrors.paxName = 'Passenger name is required';
      isValid = false;
    }
    if (!ticketData.airlineCode) {
      newErrors.airlineCode = 'Airline code is required';
      isValid = false;
    }
    if (!ticketData.airFare) {
      newErrors.airFare = 'Air fare is required';
      isValid = false;
    } else if (isNaN(parseFloat(ticketData.airFare))) {
      newErrors.airFare = 'Air fare must be a number';
      isValid = false;
    }
    setErrors(prev => ({
      ...prev,
      ...newErrors
    }));
    return isValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Format data for submission
      const formattedData = {
        ...ticket,
        ...ticketData,
        airFare: parseFloat(ticketData.airFare),
        tax: ticketData.tax ? parseFloat(ticketData.tax) : 0,
        totalCost: parseFloat(ticketData.airFare) + (ticketData.tax ? parseFloat(ticketData.tax) : 0)
      };
      onSave(formattedData);
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" data-id="element-237">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" data-id="element-238">
        <div className="flex items-start justify-between p-4 border-b border-gray-200" data-id="element-239">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-240">
            Edit Ticket #{ticket?.id}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close" data-id="element-241">
            <XIcon className="h-6 w-6" data-id="element-242" />
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-243">
          <div className="p-6 space-y-6" data-id="element-244">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-245">
              <div data-id="element-246">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-247">
                  Ticket Number <span className="text-red-500" data-id="element-248">*</span>
                </label>
                <input type="text" name="ticketNumber" value={ticketData.ticketNumber} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.ticketNumber ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. 1601234567890" data-id="element-249" />
                {errors.ticketNumber && <p className="mt-1 text-sm text-red-600" data-id="element-250">
                    {errors.ticketNumber}
                  </p>}
                <p className="mt-1 text-xs text-gray-500" data-id="element-251">
                  Enter a 13-digit ticket number
                </p>
              </div>
              <div data-id="element-252">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-253">
                  Passenger Name <span className="text-red-500" data-id="element-254">*</span>
                </label>
                <input type="text" name="paxName" value={ticketData.paxName} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.paxName ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. LOH/POHLEE MS" data-id="element-255" />
                {errors.paxName && <p className="mt-1 text-sm text-red-600" data-id="element-256">{errors.paxName}</p>}
              </div>
              <div data-id="element-257">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-258">
                  Sector Code
                </label>
                <input type="text" name="sectorCode" value={ticketData.sectorCode} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. SINCAI" data-id="element-259" />
              </div>
              <div data-id="element-260">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-261">
                  Sector
                </label>
                <input type="text" name="sector" value={ticketData.sector} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. SIN/CAI/SIN" data-id="element-262" />
              </div>
              <div data-id="element-263">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-264">
                  Airline Code <span className="text-red-500" data-id="element-265">*</span>
                </label>
                <input type="text" name="airlineCode" value={ticketData.airlineCode} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.airlineCode ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. QR" data-id="element-266" />
                {errors.airlineCode && <p className="mt-1 text-sm text-red-600" data-id="element-267">
                    {errors.airlineCode}
                  </p>}
              </div>
              <div data-id="element-268">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-269">
                  Class
                </label>
                <input type="text" name="class" value={ticketData.class} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. G" data-id="element-270" />
              </div>
              <div data-id="element-271">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-272">
                  Issue Date
                </label>
                <input type="date" name="issueDate" value={ticketData.issueDate} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-273" />
              </div>
              <div data-id="element-274">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-275">
                  Other Booking Ref
                </label>
                <input type="text" name="otherBkgRef" value={ticketData.otherBkgRef} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. QGDCIP" data-id="element-276" />
              </div>
              <div data-id="element-277">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-278">
                  Air Fare <span className="text-red-500" data-id="element-279">*</span>
                </label>
                <div className="relative" data-id="element-280">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-281">
                    <span className="text-gray-500 sm:text-sm" data-id="element-282">$</span>
                  </div>
                  <input type="text" name="airFare" value={ticketData.airFare} onChange={handleChange} className={`mt-1 block w-full pl-7 pr-3 py-2 border ${errors.airFare ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="0.00" data-id="element-283" />
                </div>
                {errors.airFare && <p className="mt-1 text-sm text-red-600" data-id="element-284">{errors.airFare}</p>}
              </div>
              <div data-id="element-285">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-286">
                  Tax
                </label>
                <div className="relative" data-id="element-287">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-288">
                    <span className="text-gray-500 sm:text-sm" data-id="element-289">$</span>
                  </div>
                  <input type="text" name="tax" value={ticketData.tax} onChange={handleChange} className="mt-1 block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="0.00" data-id="element-290" />
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-291">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-292">
                  Remarks
                </label>
                <textarea name="remark" value={ticketData.remark} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Additional details about this ticket" data-id="element-293" />
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200" data-id="element-294">
            <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-295">
              Update Ticket
            </button>
            <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-296">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default EditTicketModal;